import React from 'react'
import axios from "axios"
import { 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Box,
  CircularProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useNavigate } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  submitButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontWeight: 'bold',
  },
  link: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const RegisterationPage = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    
    const initialState = {
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        is_admin: false
    }

    const [state, setState] = React.useState(initialState)
    const [isError, setIsError] = React.useState(false)
    const [errorMessage, setErrorMessage] = React.useState("")
    const [isLoading, setIsLoading] = React.useState(false)
    const [isRegistered, setIsRegistered] = React.useState(false)

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setState(prev => ({
            ...prev, 
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const validateForm = () => {
        if (!state.username || !state.email || !state.password || !state.confirmPassword) {
            setErrorMessage("All fields are required");
            return false;
        }

        if (state.password.length < 6) {
            setErrorMessage("Password must be at least 6 characters long");
            return false;
        }

        if (state.password !== state.confirmPassword) {
            setErrorMessage("Passwords do not match");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(state.email)) {
            setErrorMessage("Please enter a valid email address");
            return false;
        }

        return true;
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            setIsError(true);
            return;
        }

        setIsLoading(true);
        setIsError(false);

        try {
            const payload = {
                username: state.username,
                email: state.email,
                password: state.password,
                is_admin: state.is_admin
            }
            const response = await axios.post("https://jtimes.zuddo.biz/auth/register", payload)
            
            if (response.status === 201) {
                setIsRegistered(true);
                
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } catch (error) {
            console.error('Registration error:', error);
            setIsError(true);
            
            if (error.response) {
    
                setErrorMessage(error.response.data.error || 'Registration failed');
            } else if (error.request) {
                setErrorMessage('Network error. Please check your connection.');
            } else {
                setErrorMessage('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    }

    const handleLoginRedirect = () => {
        navigate('/login'); 
    }

    return (
        <div className={classes.root}>
            <Paper className={classes.paper} elevation={3}>
                <Typography variant="h4" component="h1" className={classes.title}>
                    Create Account
                </Typography>

                {isError && (
                    <Typography severity="error" style={{ marginBottom: '16px' }}>
                        {errorMessage}
                    </Typography>
                )}

                {isRegistered ? (
                    <Typography severity="success" style={{ marginBottom: '16px' }}>
                        Registration successful! Redirecting to login...
                    </Typography>
                ) : (
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <TextField
                            label="Username"
                            name="username"
                            value={state.username}
                            onChange={handleChange}
                            required
                            fullWidth
                            variant="outlined"
                            disabled={isLoading}
                        />
                        
                        <TextField
                            label="Email Address"
                            name="email"
                            type="email"
                            value={state.email}
                            onChange={handleChange}
                            required
                            fullWidth
                            variant="outlined"
                            disabled={isLoading}
                        />
                        
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={state.password}
                            onChange={handleChange}
                            required
                            fullWidth
                            variant="outlined"
                            disabled={isLoading}
                            helperText="Must be at least 6 characters"
                        />
                        
                        <TextField
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={state.confirmPassword}
                            onChange={handleChange}
                            required
                            fullWidth
                            variant="outlined"
                            disabled={isLoading}
                        />

                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Account Type</InputLabel>
                            <Select
                                name="is_admin"
                                value={state.is_admin}
                                onChange={(e) => setState(prev => ({ 
                                    ...prev, 
                                    is_admin: e.target.value 
                                }))}
                                label="Account Type"
                                disabled={isLoading}
                            >
                                <MenuItem value={false}>Reader</MenuItem>
                                <MenuItem value={true}>Admin</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.submitButton}
                            disabled={isLoading}
                            fullWidth
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Create Account'
                            )}
                        </Button>
                    </form>
                )}

                <Box className={classes.link} onClick={handleLoginRedirect}>
                    <Typography variant="body2">
                        Already have an account? Sign in
                    </Typography>
                </Box>
            </Paper>
        </div>
    )
}

export { RegisterationPage };